// Cola de ventas y operaciones de caja offline. Persiste en IndexedDB (idb-keyval).
// Al volver la conexión se re-envían en orden FIFO.
import { get, set, del } from "idb-keyval";
import { api } from "@/lib/api";

const KEY = "aipos_offline_sales_v1";
const MAP_KEY = "aipos_offline_idmap_v1";

async function readQueue() {
  return (await get(KEY)) || [];
}

async function writeQueue(list) {
  if (!list.length) await del(KEY);
  else await set(KEY, list);
}

export async function enqueueSale(payload) {
  const list = await readQueue();
  const created_at = new Date().toISOString();
  const item = {
    offline_id: `OFF-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    created_at,
    kind: "SALE",
    endpoint: "/sales",
    payload: { ...payload, offline: true, offline_at: created_at },
    status: "PENDING",
    error: null,
    tries: 0,
  };
  list.push(item);
  await writeQueue(list);
  return item;
}

/**
 * Encola cualquier otra operación de caja (apertura, cierre, ingreso/retiro).
 * `meta.temp_shift_id` permite mapear el turno temporal al real al sincronizar.
 */
export async function enqueueOp(kind, endpoint, payload, meta = {}) {
  const list = await readQueue();
  const item = {
    offline_id: `OFF-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`,
    created_at: new Date().toISOString(),
    kind,
    endpoint,
    payload,
    ...meta,
    status: "PENDING",
    error: null,
    tries: 0,
  };
  list.push(item);
  await writeQueue(list);
  return item;
}

/** Unidades ya vendidas offline por producto (para mostrar stock estimado). */
export async function queuedUnits() {
  const list = await readQueue();
  const map = {};
  for (const it of list) {
    if (it.status === "SYNCED") continue;
    if (it.kind && it.kind !== "SALE") continue;
    for (const x of (it.payload?.items || [])) {
      if (!x.product_id) continue;
      map[x.product_id] = (map[x.product_id] || 0) + (Number(x.cantidad) || 0);
    }
  }
  return map;
}

/** Resumen local del turno (para poder cerrar caja sin conexión). */
export async function shiftSummary(shiftId) {
  const list = await readQueue();
  const out = { num_ventas: 0, efectivo: 0, ingresos: 0, retiros: 0, total: 0 };
  for (const it of list) {
    if (it.status === "SYNCED") continue;
    const p = it.payload || {};
    if (p.shift_id && shiftId && p.shift_id !== shiftId) continue;
    if (it.kind === "SALE" || !it.kind) {
      // El payload no trae el total (lo calcula el servidor): lo derivamos de los ítems
      const porItems = (p.items || []).reduce(
        (s, x) => s + (Number(x.cantidad) || 0) * (Number(x.precio_unitario) || 0), 0,
      ) - (Number(p.descuento) || 0);
      const monto = Number(p.total_pagar ?? p.total ?? porItems) || 0;
      out.num_ventas += 1;
      out.total += monto;
      const comps = p.payments || [];
      if (comps.length) {
        for (const c of comps) {
          if (String(c.codigo || "").toUpperCase() === "EFECTIVO") out.efectivo += Number(c.monto || 0) || 0;
        }
      } else if (String(p.payment_method || "").toUpperCase() === "EFECTIVO") {
        out.efectivo += monto;
      }
    } else if (it.kind === "CASH_MOVEMENT") {
      if (String(p.tipo).toUpperCase() === "INGRESO") out.ingresos += Number(p.monto || 0) || 0;
      else out.retiros += Number(p.monto || 0) || 0;
    }
  }
  return out;
}

export async function listPending() {
  return await readQueue();
}

export async function countPending() {
  const list = await readQueue();
  return list.filter((x) => x.status !== "SYNCED").length;
}

export async function clearSynced() {
  const list = await readQueue();
  await writeQueue(list.filter((x) => x.status !== "SYNCED"));
}

/**
 * Intenta sincronizar todas las ventas pendientes.
 * Retorna { synced, failed, remaining }.
 */
let flushing = false;   // evita que dos disparos (evento "online" + auto-sync) dupliquen operaciones

export async function flushQueue(onProgress) {
  if (flushing) {
    const pend = (await readQueue()).filter((x) => x.status !== "SYNCED").length;
    return { synced: 0, failed: 0, remaining: pend, busy: true };
  }
  flushing = true;
  try {
    return await _flush(onProgress);
  } finally { flushing = false; }
}

async function _flush(onProgress) {
  const list = await readQueue();
  const idMap = (await get(MAP_KEY)) || {};
  let synced = 0, failed = 0;
  for (const item of list) {
    if (item.status === "SYNCED") continue;
    try {
      // Si el turno se abrió offline, reemplazamos el id temporal por el real ya sincronizado
      const payload = { ...(item.payload || {}) };
      if (payload.shift_id && idMap[payload.shift_id]) payload.shift_id = idMap[payload.shift_id];
      const r = await api.post(item.endpoint || "/sales", payload);
      item.status = "SYNCED";
      item.sale_id = r.data?.sale_id;
      item.numero = r.data?.numero;
      item.error = null;
      if (item.temp_shift_id && r.data?.shift_id) {
        idMap[item.temp_shift_id] = r.data.shift_id;
        await set(MAP_KEY, idMap);
      }
      synced++;
      await writeQueue(list);   // persistimos de inmediato: si se corta la luz no se reenvía
    } catch (e) {
      item.status = "FAILED";
      item.tries = (item.tries || 0) + 1;
      item.error = e?.response?.data?.detail || e?.message || "Error de red";
      failed++;
      // Si es un error de red (sin response), abortamos para no perder orden
      if (!e?.response) {
        break;
      }
    }
    if (onProgress) onProgress({ synced, failed });
  }
  await writeQueue(list);
  const remaining = (await readQueue()).filter((x) => x.status !== "SYNCED").length;
  return { synced, failed, remaining };
}

/** Elimina un item por offline_id (útil para descartar manualmente ventas con error persistente). */
export async function discardItem(offline_id) {
  const list = await readQueue();
  await writeQueue(list.filter((x) => x.offline_id !== offline_id));
}

/** Marca todas las synced como purgables y las elimina. */
export async function purgeSynced() {
  const list = await readQueue();
  await writeQueue(list.filter((x) => x.status !== "SYNCED"));
}
